import { createFetchClient } from '@bank/fetch';

import type { CookieClient, CookieClientOptions, SyncOptions } from './types';
import { fetchBenefitPrograms, fetchCustomerCookieData } from './api';
import { writeBenefitCookie, type BenefitProgram } from './benefitCookie';
import { writeBirthYearCookie } from './birthYearCookie';
import { writeGenderCookie } from './genderCookie';
import { syncStbIdCookie } from './stbIdCookie';
import { writeStorebrandBenefitsCookie } from './storebrandBenefitsCookie';
import {
  buildCookieSnapshot,
  getCookie,
  getExpiresInMonths,
  MANAGED_COOKIE_NAMES,
  setCookie,
} from './utils';

const SYNC_TTL_MS = 86_400_000; // 24 hours

/**
 * Creates a cookie client that manages Storebrand cookies.
 *
 * @example
 * ```ts
 * const cookies = createCookieClient({
 *   clientId: window.env.CLIENT_ID,
 *   apiRoot: window.env.API_ROOT,
 *   cookieDomain: window.env.COOKIE_DOMAIN,
 *   getToken: () => auth.getToken(),
 * });
 *
 * const snapshot = await cookies.syncAll();
 * console.log(snapshot.gender, snapshot.birthYear);
 *
 * const forced = await cookies.syncAll({ force: true }); // bypass 24h cache
 * ```
 */
export const createCookieClient = (
  options: CookieClientOptions,
): CookieClient => {
  const {
    clientId,
    apiRoot,
    cookieDomain,
    path = '/',
    getToken,
    onError,
  } = options;

  const client = createFetchClient({ clientId, getToken });

  const cookieOptions = { cookieDomain, path };

  // Never let a faulty consumer-supplied `onError` break the sync flow:
  // `syncAll` must not throw or abort remaining cookie writes if logging fails.
  const report = (message: string, cause: unknown) => {
    try {
      onError?.(new Error(message, { cause }));
    } catch {
      // Intentionally ignored — reporting must never affect cookie syncing.
    }
  };

  return {
    syncAll: async ({ force = false }: SyncOptions = {}) => {
      const lastSync = getCookie(MANAGED_COOKIE_NAMES.lastSync);
      const timeSinceLastSync = Date.now() - Number(lastSync);
      const isCacheValid =
        !force && lastSync && timeSinceLastSync < SYNC_TTL_MS;

      if (isCacheValid) {
        return buildCookieSnapshot();
      }

      const [benefitsResult, customerResult, stbIdResult] =
        await Promise.allSettled([
          fetchBenefitPrograms(client, apiRoot),
          fetchCustomerCookieData(client, apiRoot),
          syncStbIdCookie(client, apiRoot, cookieOptions),
        ]);

      const programs: BenefitProgram[] =
        benefitsResult.status === 'fulfilled' &&
        benefitsResult.value.status !== 204
          ? (benefitsResult.value.data?.programs ?? [])
          : [];

      if (benefitsResult.status === 'fulfilled') {
        try {
          writeBenefitCookie(programs, cookieOptions);
        } catch (error) {
          report('Failed to write benefit cookie', error);
        }
      } else {
        report('Failed to fetch benefit programs', benefitsResult.reason);
      }

      if (customerResult.status === 'fulfilled') {
        const body = customerResult.value.data;
        const customer = body?.data?.customer;
        const coreInformation = customer?.coreInformation;

        if (coreInformation) {
          try {
            const isFramMember = !!customer.consentInformation?.focus?.value;

            writeGenderCookie(coreInformation.gender.value, cookieOptions);
            writeBirthYearCookie(
              new Date(coreInformation.birthDate.value).getFullYear(),
              cookieOptions,
            );
            writeStorebrandBenefitsCookie(
              programs,
              isFramMember,
              cookieOptions,
            );
          } catch (error) {
            report('Failed to write customer cookies', error);
          }
        } else {
          const graphqlErrors = body?.errors
            ?.map(({ message }) => message)
            .join('; ');

          report(
            'Customer cookie response was missing `data.data.customer.coreInformation`',
            graphqlErrors ?? `HTTP ${customerResult.value.status}`,
          );
        }
      } else {
        report('Failed to fetch customer cookie data', customerResult.reason);
      }

      if (stbIdResult.status === 'rejected') {
        report('Failed to sync stb_id cookie', stbIdResult.reason);
      }

      setCookie(
        MANAGED_COOKIE_NAMES.lastSync,
        String(Date.now()),
        cookieOptions,
        getExpiresInMonths(1),
      );

      return buildCookieSnapshot();
    },
  };
};
